import { t } from 'i18next';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import Sticky from 'react-stickynode';
import { ImpersonationBanner } from '../../../../../../shared/frontend/components/mainNav/impersonationBanner';
import { LargeAuthBarLayout } from '../../../../../../shared/frontend/components/mainNav/largeAuthBarLayout';
import { SmallAuthBarLayout } from '../../../../../../shared/frontend/components/mainNav/smallAuthBarLayout';
import { Media } from '../../../../../../shared/frontend/components/presentational/media';
import { SiteSwitcherBar } from '../../../../../../shared/frontend/components/siteSwitcherBar';
import { authStateSelector, siteMetaSelector } from '../../../../../../shared/frontend/scripts/selectors/global';
import { siteConfigSelector } from '../../../../../../shared/frontend/scripts/selectors/global';
import SiteBanner from '../../../../../../shared/frontend/components/presentational/siteBanner';
import { namedRoutes } from '../../bundles/routes';
import {
  LargeAuthBarAuthenticatedColumns,
  LargeAuthBarUnauthenticatedColumns,
  SmallAuthBarAuthenticatedColumns,
  SmallAuthBarUnauthenticatedColumns
} from './authBarComponents';
import LoadableMobileSiteNavMenu from './loadableMobileSiteNavMenu';
import { SiteLanguageSelector } from '../../../../../../shared/frontend/components/mainNav/siteLanguageSelector';

const LOGO_PATH = '/images/brand/Logo_Pride_Classic.png';

export const AuthBar: FC<void> = () => {
  const location = useLocation();
  const [isMenuToggled, setMenuToggled] = useState(false);
  const siteConfig = useSelector(siteConfigSelector);
  const siteMeta = useSelector(siteMetaSelector);
  const auth = useSelector(authStateSelector) || {};

  return (
    <>
      <Media key="lg-menu" lessThan="lg">
        <LoadableMobileSiteNavMenu
          isMenuToggled={isMenuToggled}
          setMenuToggled={setMenuToggled}
          raids={[]}
          regions={[]}
        />
      </Media>

      <div className="rio-site-switch--container slds-container--fluid slds-container--center rio-hidden--small">
        <div className="slds-container--x-large slds-container--center">
          <div className="slds-grid slds-grid--align-spread">
            <div className="slds-col">
              <SiteSwitcherBar className="slds-p-left--xx-small" game={siteMeta.game} />
            </div>
            <div className="slds-col">
              <SiteLanguageSelector useBuiltinPlacements={true} />
            </div>
          </div>
        </div>
      </div>

      <Sticky key="sticky-bar" innerZ={10} enabled={auth.authenticated ? true : false}>
        <div className="login-bar slds-container--fluid slds-container--center">
          {siteConfig.isImpersonating && <ImpersonationBanner username={auth.name} />}
          <div className="slds-container--x-large slds-container--center">
            <Media greaterThanOrEqual="lg">
              <LargeAuthBarLayout
                logoPath={LOGO_PATH}
                logoWidth={145}
                logoHeight={32}
                streamingNoticeClickUrl={`/search?type=guild&raiding.${siteConfig.currentRaidSlug}.normal.progress[0][gte]=0&streaming[0][eq]=1`}
                additionalColumnsComponent={
                  auth.authenticated ? LargeAuthBarAuthenticatedColumns : LargeAuthBarUnauthenticatedColumns
                }
              />
            </Media>
            <Media lessThan="lg">
              <SmallAuthBarLayout
                toggleMenu={() => setMenuToggled((val) => !val)}
                isMenuToggled={isMenuToggled}
                additionalColumnsComponent={
                  auth.authenticated ? SmallAuthBarAuthenticatedColumns : SmallAuthBarUnauthenticatedColumns
                }
              />
            </Media>
          </div>
        </div>
      </Sticky>

      <div key="container" className="slds-container--x-large slds-container--center">
        {siteConfig.maintenance && (
          <div className="slds-text-align--center slds-p-around--large">
            <div className="slds-text-heading--large text-warn text-bold">
              <i className="fa fa-exclamation-triangle" /> {t('common:site-maintenance-in-progress')}
            </div>
            <h3 className="slds-text-heading--small">{t('common:site-maintenance-read-only')}</h3>
          </div>
        )}

        <SiteBanner
          banners={siteConfig.banners}
          currentLocale={siteConfig.localeCode || 'en'}
          location={location}
          namedRoutes={namedRoutes}
        />
      </div>
    </>
  );
};
